'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import {
  Star, Search, CheckCircle, ThumbsUp, ThumbsDown, Trophy, Zap,
  Award, ChevronRight, Loader2, AlertCircle, Gift, TrendingUp, Users
} from 'lucide-react';
import { useAuth } from '@/lib/authContext';
import { fetchCollegeReviews, submitReview, CollegeReview } from '@/lib/services/reviewService';
import { awardPointsForReview } from '@/lib/services/leaderboardService';
import { createClient } from '@/lib/supabase/client';

interface College {
  id: string;
  name: string;
  location: string;
  state: string;
  shortName?: string;
}

const ratingCategories = [
  { key: 'academics', label: 'Academics & Faculty', icon: '📚' },
  { key: 'infrastructure', label: 'Infrastructure & Campus', icon: '🏛️' },
  { key: 'placements', label: 'Placements & Internships', icon: '💼' },
  { key: 'hostel', label: 'Hostel & Facilities', icon: '🏠' },
  { key: 'social', label: 'Social Life & Culture', icon: '🎉' },
  { key: 'value', label: 'Value for Money', icon: '💰' },
];

const PRIZES = [
  { rank: '🥇 #1', prize: '₹10,000 Amazon Gift Card', color: 'from-amber-400 to-yellow-300', border: 'border-amber-300' },
  { rank: '🥈 #2', prize: '₹5,000 Flipkart Voucher', color: 'from-slate-400 to-slate-300', border: 'border-slate-300' },
  { rank: '🥉 #3', prize: '₹2,500 Swiggy Credits', color: 'from-orange-400 to-amber-300', border: 'border-orange-300' },
  { rank: '🏅 Top 10', prize: 'EduPortal Premium (1 Year)', color: 'from-primary/70 to-primary/40', border: 'border-primary/30' },
];

interface CategoryRatings {
  [key: string]: number;
}

function StarInput({ value, onChange, size = 32 }: { value: number; onChange: (v: number) => void; size?: number }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(s)}
          className="transition-transform hover:scale-110"
        >
          <Star
            size={size}
            className={`transition-colors ${(hover || value) >= s ? 'text-amber-400 fill-amber-400' : 'text-border'}`}
          />
        </button>
      ))}
      {value > 0 && (
        <span className="ml-1 text-sm font-bold text-foreground self-center">
          {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][value]}
        </span>
      )}
    </div>
  );
}

export default function WriteReviewPage() {
  const { user, profile } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [colleges, setColleges] = useState<College[]>([]);
  const [selectedCollege, setSelectedCollege] = useState<College | null>(null);
  const [collegeSearch, setCollegeSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [overallRating, setOverallRating] = useState(0);
  const [categoryRatings, setCategoryRatings] = useState<CategoryRatings>({});
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [pros, setPros] = useState('');
  const [cons, setCons] = useState('');
  const [batch, setBatch] = useState('');
  const [program, setProgram] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [recentReviews, setRecentReviews] = useState<CollegeReview[]>([]);
  const [loadingColleges, setLoadingColleges] = useState(false);
  const [error, setError] = useState('');

  // Fetch colleges from Supabase
  const fetchColleges = useCallback(async (q: string) => {
    if (!q.trim()) { setColleges([]); return; }
    setLoadingColleges(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('colleges')
      .select('id, name, location, state, short_name')
      .ilike('name', `%${q}%`)
      .limit(8);
    setColleges((data || []).map((c: any) => ({
      id: c.id, name: c.name, location: c.location, state: c.state, shortName: c.short_name,
    })));
    setLoadingColleges(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchColleges(collegeSearch), 300);
    return () => clearTimeout(t);
  }, [collegeSearch, fetchColleges]);

  // Load recent reviews for sidebar
  useEffect(() => {
    if (selectedCollege) {
      fetchCollegeReviews(selectedCollege.id).then((reviews) => setRecentReviews(reviews.slice(0, 3)));
    }
  }, [selectedCollege]);

  const handleCategoryRating = (key: string, val: number) => {
    setCategoryRatings((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmit = async () => {
    if (!selectedCollege || !overallRating || !reviewText || !user) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await submitReview({
        collegeId: selectedCollege.id,
        userId: user.id,
        overallRating,
        title: reviewTitle,
        pros,
        cons,
        academics: categoryRatings['academics'] || 0,
        faculty: categoryRatings['social'] || 0,
        infrastructure: categoryRatings['infrastructure'] || 0,
        placements: categoryRatings['placements'] || 0,
        hostel: categoryRatings['hostel'] || 0,
        valueForMoney: categoryRatings['value'] || 0,
        batchYear: batch ? parseInt(batch) : undefined,
        course: program,
      });
      if (result) {
        // Award points immediately (50 for submission, more on publish)
        await awardPointsForReview(user.id, result.id);
        setEarnedPoints(50);
        setSubmitted(true);
      } else {
        setError('Failed to submit review. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setSubmitted(false);
    setStep(1);
    setSelectedCollege(null);
    setCollegeSearch('');
    setOverallRating(0);
    setCategoryRatings({});
    setReviewTitle('');
    setReviewText('');
    setPros('');
    setCons('');
    setBatch('');
    setProgram('');
    setEarnedPoints(0);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-20">
          <div className="text-center max-w-lg">
            <div className="relative inline-flex mb-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <CheckCircle size={44} className="text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center shadow-lg">
                <Zap size={18} className="text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-extrabold text-foreground mb-2">Review Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Your review for <strong>{selectedCollege?.name}</strong> is pending approval. Once published, you'll earn more points!
            </p>

            {/* Points earned */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-5 mb-6">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Trophy size={24} className="text-amber-500" />
                <span className="text-xl font-extrabold text-amber-700">+{earnedPoints} Points Earned!</span>
              </div>
              <p className="text-sm text-amber-700 mb-3">Earn more points when your review gets approved & helpful votes!</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white rounded-xl p-2 border border-amber-100">
                  <p className="text-lg font-extrabold text-amber-600">50</p>
                  <p className="text-xs text-muted-foreground">Submission</p>
                </div>
                <div className="bg-white rounded-xl p-2 border border-amber-100">
                  <p className="text-lg font-extrabold text-amber-600">+50</p>
                  <p className="text-xs text-muted-foreground">On Publish</p>
                </div>
                <div className="bg-white rounded-xl p-2 border border-amber-100">
                  <p className="text-lg font-extrabold text-amber-600">+5</p>
                  <p className="text-xs text-muted-foreground">Per 10 Votes</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={resetForm} className="btn-secondary px-6 py-3">
                Write Another Review
              </button>
              <Link href="/influencer-leaderboard" className="btn-primary px-6 py-3 flex items-center gap-2 justify-center">
                <Trophy size={16} /> View Leaderboard
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">

        {/* Hero */}
        <section className="bg-secondary py-12 px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm text-slate-200 mb-4 border border-white/20">
                  <Star size={14} className="text-primary fill-primary" />
                  <span>Help students make better decisions</span>
                </div>
                <h1 className="text-3xl lg:text-4xl font-extrabold text-white mb-3">
                  Share Your College Experience
                </h1>
                <p className="text-slate-300">Write a review, earn points, and climb the Influencer Leaderboard. Top reviewers win exciting prizes!</p>
              </div>
              {/* Points teaser */}
              <div className="flex gap-3 shrink-0">
                {[
                  { icon: <Zap size={18} className="text-amber-400" />, label: '50 pts', sub: 'per review' },
                  { icon: <ThumbsUp size={18} className="text-green-400" />, label: '+5 pts', sub: 'per 10 votes' },
                  { icon: <Gift size={18} className="text-pink-400" />, label: 'Prizes', sub: 'for top 10' },
                ].map((item, i) => (
                  <div key={i} className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-center">
                    <div className="flex justify-center mb-1">{item.icon}</div>
                    <p className="text-white font-extrabold text-sm">{item.label}</p>
                    <p className="text-slate-400 text-xs">{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Review Form */}
            <div className="lg:col-span-2">
              {/* Progress */}
              <div className="flex items-center gap-3 mb-8">
                {[1, 2, 3].map((s) => (
                  <React.Fragment key={s}>
                    <div className={`flex items-center gap-2 ${step >= s ? 'text-primary' : 'text-muted-foreground'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${step > s ? 'bg-primary border-primary text-white' : step === s ? 'border-primary text-primary' : 'border-border text-muted-foreground'}`}>
                        {step > s ? '✓' : s}
                      </div>
                      <span className="text-sm font-semibold hidden sm:block">
                        {s === 1 ? 'Select College' : s === 2 ? 'Rate & Review' : 'Your Details'}
                      </span>
                    </div>
                    {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-primary' : 'bg-border'}`} />}
                  </React.Fragment>
                ))}
              </div>

              {/* Not logged in warning */}
              {!user && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                  <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Sign in to submit a review</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      <Link href="/sign-in" className="underline font-bold">Sign in</Link> or <Link href="/sign-up" className="underline font-bold">create an account</Link> to earn points and rank on the leaderboard.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 1 */}
              {step === 1 && (
                <div className="bg-card rounded-2xl border border-border p-6">
                  <h2 className="text-xl font-bold text-foreground mb-5">Which college are you reviewing?</h2>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <input
                      type="text"
                      placeholder="Search your college..."
                      value={collegeSearch}
                      onChange={(e) => { setCollegeSearch(e.target.value); setShowDropdown(true); setSelectedCollege(null); }}
                      onFocus={() => setShowDropdown(true)}
                      className="input-base pl-9"
                    />
                    {showDropdown && collegeSearch && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg z-10 overflow-hidden">
                        {loadingColleges ? (
                          <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                            <Loader2 size={14} className="animate-spin" /> Searching...
                          </div>
                        ) : colleges.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-muted-foreground">No colleges found</div>
                        ) : (
                          colleges.map((c) => (
                            <button
                              key={c.id}
                              onClick={() => { setSelectedCollege(c); setCollegeSearch(c.name); setShowDropdown(false); }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted transition-colors"
                            >
                              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                                {(c.shortName || c.name).slice(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-semibold text-foreground">{c.name}</p>
                                <p className="text-xs text-muted-foreground">{c.location}, {c.state}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {selectedCollege && (
                    <div className="mt-4 flex items-center gap-3 p-4 bg-accent rounded-xl border border-primary/20">
                      <CheckCircle size={20} className="text-primary shrink-0" />
                      <div>
                        <p className="font-semibold text-foreground text-sm">{selectedCollege.name}</p>
                        <p className="text-xs text-muted-foreground">{selectedCollege.location}, {selectedCollege.state}</p>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => selectedCollege && setStep(2)}
                    disabled={!selectedCollege}
                    className="btn-primary mt-6 w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue →
                  </button>
                </div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
                  <h2 className="text-xl font-bold text-foreground">
                    Rate your experience at <span className="text-primary">{selectedCollege?.name}</span>
                  </h2>

                  {/* Overall Rating */}
                  <div>
                    <p className="font-semibold text-foreground mb-3">Overall Rating <span className="text-danger">*</span></p>
                    <StarInput value={overallRating} onChange={setOverallRating} size={36} />
                  </div>

                  {/* Category Ratings */}
                  <div>
                    <p className="font-semibold text-foreground mb-3">Rate by Category</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {ratingCategories.map((cat) => (
                        <div key={cat.key} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                          <span className="text-xl">{cat.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-foreground truncate">{cat.label}</p>
                            <div className="flex gap-1 mt-1">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <button key={s} type="button" onClick={() => handleCategoryRating(cat.key, s)}>
                                  <Star size={14} className={`transition-colors ${(categoryRatings[cat.key] || 0) >= s ? 'text-amber-400 fill-amber-400' : 'text-border'}`} />
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Review Title */}
                  <div>
                    <p className="font-semibold text-foreground mb-2">Review Title</p>
                    <input
                      type="text"
                      value={reviewTitle}
                      onChange={(e) => setReviewTitle(e.target.value)}
                      placeholder="Summarize your experience in one line..."
                      className="input-base"
                      maxLength={100}
                    />
                  </div>

                  {/* Review Text */}
                  <div>
                    <p className="font-semibold text-foreground mb-2">Detailed Review <span className="text-danger">*</span></p>
                    <textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value.slice(0, 1000))}
                      placeholder="Share your experience — academics, campus life, placements, faculty, hostel..."
                      rows={5}
                      className="input-base resize-none"
                    />
                    <p className={`text-xs mt-1 ${reviewText.length < 100 ? 'text-muted-foreground' : 'text-green-600'}`}>
                      {reviewText.length}/1000 characters (min. 100)
                    </p>
                  </div>

                  {/* Pros & Cons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-green-700 mb-2">
                        <ThumbsUp size={14} /> Pros
                      </label>
                      <textarea value={pros} onChange={(e) => setPros(e.target.value)} placeholder="What did you love?" rows={3} className="input-base resize-none text-sm" />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-red-600 mb-2">
                        <ThumbsDown size={14} /> Cons
                      </label>
                      <textarea value={cons} onChange={(e) => setCons(e.target.value)} placeholder="What could be better?" rows={3} className="input-base resize-none text-sm" />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => setStep(1)} className="btn-secondary px-6 py-3">← Back</button>
                    <button
                      onClick={() => overallRating > 0 && reviewText.length >= 100 && setStep(3)}
                      disabled={!overallRating || reviewText.length < 100}
                      className="btn-primary flex-1 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <div className="bg-card rounded-2xl border border-border p-6 space-y-5">
                  <h2 className="text-xl font-bold text-foreground">Almost done! Tell us about yourself</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-foreground mb-1.5 block">Batch / Year of Passing</label>
                      <input type="text" value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="e.g. 2023" className="input-base" maxLength={4} />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-foreground mb-1.5 block">Program</label>
                      <input type="text" value={program} onChange={(e) => setProgram(e.target.value)} placeholder="e.g. B.Tech CSE" className="input-base" />
                    </div>
                  </div>

                  {/* Review Summary */}
                  <div className="bg-muted rounded-xl p-4 text-sm text-muted-foreground space-y-1">
                    <p className="font-semibold text-foreground mb-2">Review Summary</p>
                    <p>College: <strong className="text-foreground">{selectedCollege?.name}</strong></p>
                    <p>Overall: <strong className="text-foreground">{overallRating}/5 ⭐</strong></p>
                    {reviewTitle && <p>Title: <strong className="text-foreground">{reviewTitle}</strong></p>}
                    <p className="line-clamp-2 mt-1">{reviewText}</p>
                  </div>

                  {/* Points reminder */}
                  <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <Zap size={18} className="text-amber-500 shrink-0" />
                    <p className="text-sm text-amber-800 font-semibold">You'll earn <span className="text-amber-600">50 points</span> for submitting this review!</p>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
                      <AlertCircle size={16} /> {error}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button onClick={() => setStep(2)} className="btn-secondary px-6 py-3">← Back</button>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting || !user}
                      className="btn-primary flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : 'Submit Review ✓'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              {/* Prizes */}
              <div className="bg-card rounded-2xl border border-border p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy size={18} className="text-amber-500" />
                  <h3 className="font-bold text-foreground">Influencer Prizes</h3>
                </div>
                <div className="space-y-2.5">
                  {PRIZES.map((p, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-xl bg-gradient-to-r ${p.color} border ${p.border}`}>
                      <span className="text-sm font-bold text-foreground">{p.rank}</span>
                      <span className="text-xs font-semibold text-foreground/80">{p.prize}</span>
                    </div>
                  ))}
                </div>
                <Link href="/influencer-leaderboard" className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-primary hover:underline">
                  View Full Leaderboard <ChevronRight size={14} />
                </Link>
              </div>

              {/* How it works */}
              <div className="bg-card rounded-2xl border border-border p-5">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp size={18} className="text-primary" />
                  <h3 className="font-bold text-foreground">How to Earn Points</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { pts: '+50', label: 'Submit a review', icon: '✍️' },
                    { pts: '+50', label: 'Review gets published', icon: '✅' },
                    { pts: '+5', label: 'Every 10 helpful votes', icon: '👍' },
                    { pts: '+10', label: 'Verified student badge', icon: '🎓' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-base">{item.icon}</span>
                      <span className="text-sm text-foreground flex-1">{item.label}</span>
                      <span className="text-sm font-extrabold text-amber-600">{item.pts}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Reviews */}
              {recentReviews.length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Users size={18} className="text-primary" />
                    <h3 className="font-bold text-foreground">Recent Reviews</h3>
                  </div>
                  <div className="space-y-3">
                    {recentReviews.map((rev) => (
                      <div key={rev.id} className="border-b border-border last:border-0 pb-3 last:pb-0">
                        <div className="flex items-start justify-between mb-1">
                          <p className="text-xs font-bold text-foreground">{rev.authorName || 'Anonymous'}</p>
                          <div className="flex items-center gap-0.5">
                            <Star size={10} className="text-amber-400 fill-amber-400" />
                            <span className="text-xs font-bold text-amber-700">{rev.overallRating}</span>
                          </div>
                        </div>
                        {rev.title && <p className="text-xs text-foreground font-semibold mb-1">{rev.title}</p>}
                        <p className="text-xs text-muted-foreground line-clamp-2">{rev.pros || 'No details provided'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
